import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Tabs,
  Tab,
  Chip,
  Autocomplete,
  TextField,
  Skeleton,
  Alert,
  Divider,
  useTheme,
  alpha,
  Theme
} from '@mui/material';
import {
  PictureAsPdfOutlined,
  CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers';
import { useSnackbar } from 'notistack';

import { useModulePermissions } from '../../hooks/useModulePermissions';
import { usePdfExport, useSchoolBranding } from '../../utils/pdf';
import AttendanceSummaryDocument from '../../components/attendance/pdf/AttendanceSummaryDocument';
import BusyIcon from '../../components/common/BusyIcon';
import { useSessionContext } from '../../context/SessionContext';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { SessionClass, SectionInstance } from '../../types/sessions';
import {
  attendanceSummaryService,
  AttendanceSummaryResponse,
  SummaryGroup,
  ExceptionPerson
} from '../../services/attendanceSummaryService';

/**
 * Attendance Summary — the "who's out today" digest.
 *
 * Two tabs (Students / Employees), each gated by its own permission. The tabs
 * only render if the caller has the corresponding permission; if only one is
 * granted, the Tabs strip collapses to a single-scope page.
 *
 * The page is deliberately exception-first: a class where everyone is present
 * still appears (as a single green "All present" line) so the reader can be
 * confident nothing is silently missing. A class with no marks yet shows a
 * grey "Not marked yet" line — again, absence-of-data is surfaced, not hidden.
 *
 * The Download PDF button always mirrors the currently-visible tab and the
 * currently-applied filters — no separate export dialog.
 */

type Scope = 'students' | 'employees';

const AttendanceSummaryPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead } = useModulePermissions();
  const { activeSession } = useSessionContext();

  const canViewStudents  = canRead('attendance_summary_students');
  const canViewEmployees = canRead('attendance_summary_employees');

  // Landing tab — whichever scope the caller can see. Both permissions grant
  // Students first (matches the sidebar order).
  const initialScope: Scope | null = canViewStudents ? 'students' : canViewEmployees ? 'employees' : null;
  const [scope, setScope] = useState<Scope | null>(initialScope);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Student filters — reference lists.
  const [classes,  setClasses]  = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [selectedClasses, setSelectedClasses]   = useState<SessionClass[]>([]);
  const [selectedSections, setSelectedSections] = useState<SectionInstance[]>([]);

  // Data + UI state.
  const [summary, setSummary] = useState<AttendanceSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dateStr = useMemo(
    () => attendanceSummaryService.formatDateForAPI(selectedDate),
    [selectedDate]
  );

  // ---- Load classes on mount (students tab only) ----
  useEffect(() => {
    if (!canViewStudents) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await classesApi.list();
        if (!cancelled) setClasses(list);
      } catch (err: any) {
        if (!cancelled) enqueueSnackbar(err?.message || 'Could not load classes', { variant: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, [canViewStudents, enqueueSnackbar]);

  // ---- Load sections whenever the class filter changes ----
  useEffect(() => {
    if (!canViewStudents || !activeSession) {
      setSections([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // If the caller narrowed the class filter, only fetch sections for
        // those classes — one request per class, run in parallel. When
        // unfiltered, we skip fetching until they pick a class (sections are
        // meaningful only relative to a class).
        if (selectedClasses.length === 0) {
          setSections([]);
          return;
        }
        const results = await Promise.all(
          selectedClasses.map(c =>
            sectionInstancesApi.list({ sessionId: activeSession._id, classId: c._id })
          )
        );
        if (cancelled) return;
        const flattened = results.flat();
        setSections(flattened);
        // Drop any previously-selected section that no longer belongs to a
        // remaining class — keeps the query consistent with what's visible.
        setSelectedSections(prev => prev.filter(s => flattened.some(f => f._id === s._id)));
      } catch (err) {
        if (!cancelled) console.error('Error loading sections:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [canViewStudents, activeSession, selectedClasses]);

  // ---- Fetch the summary on scope / date / filter change ----
  const loadSummary = useCallback(async () => {
    if (!scope) return;
    try {
      setLoading(true);
      setError(null);
      const data = scope === 'students'
        ? await attendanceSummaryService.getStudentSummary({
            date: dateStr,
            classIds: selectedClasses.map(c => c._id),
            sectionInstanceIds: selectedSections.map(s => s._id)
          })
        : await attendanceSummaryService.getEmployeeSummary(dateStr);
      setSummary(data);
    } catch (err: any) {
      setSummary(null);
      setError(err?.response?.data?.message || err?.message || 'Failed to load attendance summary.');
    } finally {
      setLoading(false);
    }
  }, [scope, dateStr, selectedClasses, selectedSections]);

  useEffect(() => { void loadSummary(); }, [loadSummary]);

  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();

  const handleDownload = useCallback(async () => {
    if (!scope || !summary) return;
    setDownloading(true);
    try {
      // Drawn in the browser from the summary on screen — the PDF is exactly what's shown.
      await generatePdf(
        <AttendanceSummaryDocument summary={summary} scope={scope} branding={branding} generatedAt={new Date().toISOString()} />,
        `attendance-summary-${scope}-${dateStr}.pdf`,
      );
    } finally {
      setDownloading(false);
    }
  }, [scope, summary, dateStr, generatePdf, branding]);

  if (!canViewStudents && !canViewEmployees) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">
          You don't have access to Attendance Summary. Ask an administrator to grant
          &quot;Student Attendance Summary&quot; or &quot;Employee Attendance Summary&quot;.
        </Alert>
      </Box>
    );
  }

  const showBothTabs = canViewStudents && canViewEmployees;
  const canDownload  = !downloading && !loading && !!summary && summary.groups.length > 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Page header — matches the plain h4 + body2 subtitle pattern used
            across the portal. Download button pinned to the right. */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} color="text.primary">
              Attendance Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Today&apos;s exceptions at a glance — who&apos;s absent, late, or on leave — grouped by
              {scope === 'employees' ? ' role.' : ' class and section.'}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<BusyIcon busy={downloading}><PictureAsPdfOutlined fontSize="small" /></BusyIcon>}
            onClick={handleDownload}
            disabled={!canDownload}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                color: theme.palette.primary.main
              },
              '&.Mui-disabled': { borderColor: theme.palette.divider }
            }}
          >
            {downloading ? 'Preparing…' : 'Download PDF'}
          </Button>
        </Box>

        {/* Scope tabs — only render when both permissions are granted. */}
        {showBothTabs && (
          <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <Tabs
              value={scope}
              onChange={(_, v) => setScope(v)}
              textColor="primary"
              indicatorColor="primary"
              sx={{ px: 1 }}
            >
              <Tab value="students"  label="Students"  sx={{ textTransform: 'none', fontWeight: 600 }} />
              <Tab value="employees" label="Employees" sx={{ textTransform: 'none', fontWeight: 600 }} />
            </Tabs>
          </Paper>
        )}

        {/* Filters */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            flexWrap="wrap"
            useFlexGap
          >
            <DatePicker
              label="Date"
              value={selectedDate}
              onChange={(d) => d && setSelectedDate(d)}
              format="dd MMM yyyy"
              slotProps={{ textField: { size: 'small', sx: { width: 200 } } }}
              slots={{ openPickerIcon: CalendarMonthIcon }}
            />

            {scope === 'students' && (
              <>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={classes}
                  value={selectedClasses}
                  onChange={(_, v) => setSelectedClasses(v)}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  renderInput={(params) => (
                    <TextField {...params} label="Classes" placeholder={selectedClasses.length ? '' : 'All classes'} />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((c, index) => {
                      const { key, ...tagProps } = getTagProps({ index }) as any;
                      return <Chip key={key} label={c.name} size="small" {...tagProps} />;
                    })
                  }
                  sx={{ minWidth: 240, flex: 1, maxWidth: 340 }}
                />

                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={sections}
                  value={selectedSections}
                  onChange={(_, v) => setSelectedSections(v)}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  disabled={selectedClasses.length === 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sections"
                      placeholder={
                        selectedClasses.length === 0
                          ? 'Pick classes first'
                          : selectedSections.length ? '' : 'All sections'
                      }
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((s, index) => {
                      const { key, ...tagProps } = getTagProps({ index }) as any;
                      return <Chip key={key} label={s.name} size="small" {...tagProps} />;
                    })
                  }
                  sx={{ minWidth: 220, flex: 1, maxWidth: 320 }}
                />
              </>
            )}
          </Stack>
        </Paper>

        {/* Body */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={68} />
            <Skeleton variant="rounded" height={140} />
            <Skeleton variant="rounded" height={140} />
          </Stack>
        ) : summary ? (
          <SummaryBody scope={scope!} summary={summary} />
        ) : null}
      </Box>
    </LocalizationProvider>
  );
};

// ---------------------------------------------------------------------------
// Body — slim totals strip + one calm card per group
// ---------------------------------------------------------------------------
//
// Density philosophy: this is a scan-first document. The user's eye should
// travel down class names on the left, catching exception counts on the right.
// Everything else — chips, reasons, identifiers — is secondary and rendered
// at 12–13px so it reads without stealing focus.
// ---------------------------------------------------------------------------

interface SummaryBodyProps {
  scope: Scope;
  summary: AttendanceSummaryResponse;
}

const SummaryBody: React.FC<SummaryBodyProps> = ({ scope, summary }) => {
  const groupLabel = scope === 'students' ? 'classes' : 'roles';

  return (
    <Box>
      <TotalsStrip
        rosters={summary.totals.rosters}
        present={summary.totals.present}
        exceptions={summary.totals.exceptions}
        notMarked={summary.totals.notMarked}
        groupLabel={groupLabel}
      />

      {summary.groups.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 5, borderRadius: 2, textAlign: 'center', borderStyle: 'dashed' }}>
          <Typography variant="body2" color="text.secondary">
            No {scope} match this filter for {summary.date}.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {summary.groups.map(g => <SummaryGroupCard key={g.key} group={g} />)}
        </Stack>
      )}
    </Box>
  );
};

// A single slim strip — labels inline with numbers, no giant h6 anymore. Reads
// like a summary sentence at the top of a report.
const TotalsStrip: React.FC<{
  rosters: number; present: number; exceptions: number; notMarked: number; groupLabel: string;
}> = ({ rosters, present, exceptions, notMarked, groupLabel }) => {
  const theme = useTheme();
  const Item: React.FC<{ n: number; label: string; color?: string }> = ({ n, label, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: color ?? 'text.primary', lineHeight: 1 }}>{n}</Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{label}</Typography>
    </Box>
  );
  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2.5,
        px: 2, py: 1.25, mb: 1.5, borderRadius: 2
      }}
    >
      <Item n={rosters} label={groupLabel} />
      <Divider orientation="vertical" flexItem />
      <Item n={present} label="present" color={theme.palette.success.dark} />
      <Divider orientation="vertical" flexItem />
      <Item n={exceptions} label={exceptions === 1 ? 'exception' : 'exceptions'} color={exceptions > 0 ? theme.palette.warning.dark : undefined} />
      <Divider orientation="vertical" flexItem />
      <Item n={notMarked} label="not marked" color={notMarked > 0 ? theme.palette.text.secondary : undefined} />
    </Paper>
  );
};

// One "class + section" or "role" block. Header carries counts; body is a
// stack of buckets (Absent → Late → Half day → On leave), each announced by
// one small status chip and followed by a numbered list of names.
const SummaryGroupCard: React.FC<{ group: SummaryGroup }> = ({ group }) => {
  const theme = useTheme();

  const buckets = useMemo(() => nonEmptyBuckets(group), [group]);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Header row — a single quiet line. */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 1.5, px: 2, py: 0.75
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
            {group.primaryLabel}
          </Typography>
          {group.secondaryLabel && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {group.secondaryLabel}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <CountPill tone="present" text={`${group.presentCount}/${group.totalRoster}`} />
          {group.exceptionCount > 0 && (
            <CountPill tone="exception" text={`${group.exceptionCount} ${group.exceptionCount === 1 ? 'exception' : 'exceptions'}`} />
          )}
          {group.notMarkedCount > 0 && !group.notMarked && (
            <CountPill tone="muted" text={`${group.notMarkedCount} not marked`} />
          )}
        </Stack>
      </Box>

      {group.allPresent ? (
        <OneLiner tone="good">All present</OneLiner>
      ) : group.notMarked ? (
        <OneLiner tone="muted">Attendance not marked yet.</OneLiner>
      ) : (
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          {buckets.map(b => (
            <BucketBlock key={b.tone} tone={b.tone} people={b.people} />
          ))}
        </Box>
      )}
    </Paper>
  );
};

// Order matters: same order the eye scans an exception digest — bad news first.
type BucketTone = 'absent' | 'late' | 'halfDay' | 'leave';

const BUCKET_ORDER: readonly BucketTone[] = ['absent', 'late', 'halfDay', 'leave'] as const;

const nonEmptyBuckets = (g: SummaryGroup): Array<{ tone: BucketTone; people: ExceptionPerson[] }> =>
  BUCKET_ORDER
    .map(tone => ({ tone, people: g.exceptions[tone] }))
    .filter(b => b.people.length > 0);

const TONE_LABEL: Record<BucketTone, string> = {
  absent: 'Absent', late: 'Late', halfDay: 'Half day', leave: 'On leave'
};

// Alpha values mirror `components/common/StatusChip.tsx` so attendance chips
// read as siblings of every other status chip in the app.
const rowChipPalette = (theme: Theme) => ({
  absent:  { bg: alpha(theme.palette.error.main,   0.12), fg: theme.palette.error.dark,   border: alpha(theme.palette.error.main,   0.32) },
  late:    { bg: alpha(theme.palette.warning.main, 0.14), fg: theme.palette.warning.dark, border: alpha(theme.palette.warning.main, 0.34) },
  halfDay: { bg: alpha(theme.palette.warning.main, 0.10), fg: theme.palette.warning.dark, border: alpha(theme.palette.warning.main, 0.26) },
  leave:   { bg: alpha(theme.palette.info.main,    0.12), fg: theme.palette.info.dark,    border: alpha(theme.palette.info.main,    0.32) }
});

// Shared shape for every chip on this page — matches StatusChip's rhythm.
const chipSx = (p: { bg: string; fg: string; border: string }) => ({
  height: 22,
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.1,
  bgcolor: p.bg,
  color:   p.fg,
  borderColor: p.border,
  '& .MuiChip-label': { px: 1 },
  flexShrink: 0
});

// Count chip used in the group header — same visual family as row chips,
// just different tone tokens (success / warning / neutral).
const CountPill: React.FC<{ text: string; tone: 'present' | 'exception' | 'muted' }> = ({ text, tone }) => {
  const theme = useTheme();
  const palette =
    tone === 'present'   ? { bg: alpha(theme.palette.success.main, 0.12), fg: theme.palette.success.dark, border: alpha(theme.palette.success.main, 0.32) } :
    tone === 'exception' ? { bg: alpha(theme.palette.warning.main, 0.14), fg: theme.palette.warning.dark, border: alpha(theme.palette.warning.main, 0.34) } :
                           { bg: alpha(theme.palette.text.secondary, 0.08), fg: theme.palette.text.secondary, border: alpha(theme.palette.text.secondary, 0.22) };
  return <Chip size="small" label={text} variant="outlined" sx={chipSx(palette)} />;
};

// Full-width status line for allPresent / notMarked short-circuits.
const OneLiner: React.FC<{ tone: 'good' | 'muted'; children: React.ReactNode }> = ({ tone, children }) => {
  const theme = useTheme();
  const style =
    tone === 'good'
      ? { color: theme.palette.success.dark, bg: alpha(theme.palette.success.main, 0.06), border: alpha(theme.palette.success.main, 0.20) }
      : { color: theme.palette.text.secondary, bg: 'transparent', border: theme.palette.divider };
  return (
    <Box sx={{
      px: 2, py: 0.9,
      borderTop: `1px solid ${style.border}`,
      bgcolor: style.bg,
      color:   style.color,
      fontSize: 12.5,
      fontStyle: tone === 'muted' ? 'italic' : 'normal'
    }}>
      {children}
    </Box>
  );
};

// One bucket = one status chip sub-header + a numbered list of people.
// The chip is stated ONCE, so the eye stops repeating the same word down the
// column. Empty buckets never render, so cards stay dense-per-bucket without
// hollow rows.
const BucketBlock: React.FC<{ tone: BucketTone; people: ExceptionPerson[] }> = ({ tone, people }) => {
  const theme = useTheme();
  const palette = rowChipPalette(theme)[tone];

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Chip
        size="small"
        label={`${TONE_LABEL[tone]} · ${people.length}`}
        variant="outlined"
        sx={chipSx(palette)}
      />
      <Stack spacing={0.1} sx={{ mt: 0.85 }}>
        {people.map((p, i) => (
          <PersonItem key={`${p.id}-${i}`} index={i + 1} person={p} tone={tone} />
        ))}
      </Stack>
    </Box>
  );
};

// One person, one numbered line under its bucket. No chip — the parent
// bucket's chip already said the status.
const PersonItem: React.FC<{
  index: number;
  person: ExceptionPerson;
  tone: BucketTone;
}> = ({ index, person, tone }) => {
  const theme = useTheme();

  return (
    <Box sx={{
      display: 'flex', alignItems: 'baseline', gap: 0.75,
      py: 0.25,
      minWidth: 0
    }}>
      <Typography sx={{
        minWidth: 22, textAlign: 'right',
        fontSize: 11.5, color: 'text.disabled',
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0
      }}>
        {index}.
      </Typography>

      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>
        {person.name}
      </Typography>

      {person.identifier && (
        <Typography sx={{ fontSize: 11.5, color: 'text.disabled', flexShrink: 0 }}>
          · {person.identifier}
        </Typography>
      )}

      {tone === 'leave' && person.leaveType && (
        <Typography sx={{ fontSize: 12, color: theme.palette.info.dark, flexShrink: 0 }}>
          · {person.leaveType}
        </Typography>
      )}

      {person.remarks && (
        <Typography sx={{
          fontSize: 12, color: 'text.secondary', fontStyle: 'italic',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0
        }} title={person.remarks}>
          — {person.remarks}
        </Typography>
      )}
    </Box>
  );
};

export default AttendanceSummaryPage;
